import BadgeFactory from '@/app/components/badge/badge-factory';
import { type LauncherUrlProps } from '@/utils/url/launcher-url';
import { GameUrl } from '@/app/components/game/subcomponents/game-url/game-url';
import { GameDetails } from '../../subcomponents/details/game-details';
import type { BadgeType } from '@/types/badges';
import { InfoButton } from '../../subcomponents/info-button';
import { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import { SwjJackpotTicker } from '@/app/components/site-wide-jackpot/subcomponents/jackpot-ticker';
import { SwjBorderOverlay } from '../../subcomponents/swj-border-overlay/swj-border-overlay';
import dynamic from 'next/dynamic';
import { gameImageSrcSets } from '@/utils/images/attributes';

import(`./styles/${process.env.THEME}.scss`);

const TRENDING_LOGO_SIZE = { mobile: 130, tablet: 130 };
const TRENDING_BACKGROUND_SIZE = { mobile: 130, tablet: 195 };

const AnimatedTile = dynamic(() =>
    import('../../subcomponents/animated-tile/animated-tile').then((mod) => mod.AnimatedTile)
);

type GamePropsUI = {
    // type?
    index: number;
    uid: string;
    name: string;
    description: string;
    launcherUrlProps: LauncherUrlProps;
    logoUrl: string;
    backgroundUrl: string;
    customBackgroundColor: string;
    label?: string;
    isPopup: boolean;
    hideGameInfo?: boolean;
    gridLayout?: PRISMIC_ZONE_LAYOUT;
    swjLabel?: 'default' | 'small';
    isSiteWideJackpot?: boolean;
    tracking: {
        gameUid: string;
        gamePosition: number | null;
        gameName: string;
        gameProvider: string;
    };
    animatedTileVideoUrl?: string | null;
};

export const TrendingGame = ({
    index,
    uid,
    name,
    description,
    launcherUrlProps,
    logoUrl,
    backgroundUrl,
    customBackgroundColor,
    label,
    hideGameInfo = false,
    swjLabel,
    isSiteWideJackpot,
    tracking,
    animatedTileVideoUrl
}: GamePropsUI) => {
    const logoAndBackground = (
        <>
            <img
                className="logo"
                src={logoUrl}
                alt="logo"
                loading="lazy"
                {...gameImageSrcSets(logoUrl, TRENDING_LOGO_SIZE)}
            />
            <img
                style={{ backgroundColor: customBackgroundColor }}
                className="background"
                src={backgroundUrl}
                alt={`game-${name}-background`}
                loading="lazy"
                {...gameImageSrcSets(backgroundUrl, TRENDING_BACKGROUND_SIZE)}
            />
        </>
    );

    return (
        <div
            className="game--trending"
            data-gameuid={uid}
            data-t-gameuid={tracking.gameUid}
            data-t-gameposition={tracking.gamePosition}
            data-t-gamename={tracking.gameName}
            data-t-gameprovider={tracking?.gameProvider}
            data-t-isswj={!!isSiteWideJackpot}
            data-t-game-tile-type={animatedTileVideoUrl ? 'animated' : 'static'}>
            <BadgeFactory badgeType="trending" value={index} isSWJTickerDisplayed={isSiteWideJackpot} />
            {label && <BadgeFactory badgeType={label as BadgeType} isSWJTickerDisplayed={isSiteWideJackpot} />}
            <InfoButton uid={uid} />
            <GameUrl launcherUrlProps={launcherUrlProps}>
                <div className="game--trending__image-container">
                    {swjLabel && <SwjJackpotTicker isSmall={swjLabel === 'small'} />}
                    <SwjBorderOverlay isSiteWideJackpot={isSiteWideJackpot} swjLabel={swjLabel} />
                    {animatedTileVideoUrl ? (
                        <AnimatedTile videoSrc={animatedTileVideoUrl} gameName={name}>
                            {logoAndBackground}
                        </AnimatedTile>
                    ) : (
                        logoAndBackground
                    )}
                </div>
                <>{!hideGameInfo && <GameDetails uid={uid} name={name} description={description} />}</>
            </GameUrl>
        </div>
    );
};
